import { Head, useForm } from "@inertiajs/react";
import { useState } from "react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  Shield,
  Calendar,
} from "lucide-react";

interface Props {
  user: {
    id: number;
    email: string;
    name?: string;
    role: string;
    created_at: string;
  };
  error?: string;
  notice?: string;
}

export default function Profile({ user, error, notice }: Props) {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { data, setData, put, processing, errors } = useForm({
    user: {
      name: user.name || "",
      current_password: "",
      password: "",
      password_confirmation: "",
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    put("/users", {
      preserveUrl: true,
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case "admin":
        return "destructive";
      case "manager":
        return "default";
      default:
        return "secondary";
    }
  };

  return (
    <Layout>
      <Head title="Profile | Mailkit - Resolute" />

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-4">
        <div className="max-w-2xl mx-auto pt-8">
          {/* Header */}
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Profile Settings
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Manage your account information and security settings
            </p>
          </div>

          {/* Profile Form */}
          <Card className="border-0">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="text-2xl">Update Profile</CardTitle>
              <CardDescription>
                Keep your account information up to date
              </CardDescription>
            </CardHeader>

            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-6">
                {/* Success/Error Messages */}
                {notice && (
                  <Alert className="border-green-200 bg-green-50 text-green-700">
                    <AlertCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-700 font-medium">
                      {notice}
                    </AlertDescription>
                  </Alert>
                )}

                {(error || errors.name || errors.email) && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      {error || errors.name || errors.email}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Basic Information Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b pb-2">
                    Basic Information
                  </h3>

                  {/* Name Field */}
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-sm font-medium">
                      Full Name
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="name"
                        type="text"
                        placeholder="Enter your full name"
                        value={data.user.name}
                        onChange={(e) =>
                          setData("user", {
                            ...data.user,
                            name: e.target.value,
                          })
                        }
                        className="pl-10 h-11"
                        autoComplete="name"
                      />
                    </div>
                  </div>

                  {/* Email Field */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium">
                      Email Address
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="email"
                        type="email"
                        value={user.email}
                        className="pl-10 h-11 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-not-allowed"
                        readOnly
                        disabled
                      />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Email address cannot be changed. Contact your
                      administrator if needed.
                    </p>
                  </div>
                </div>

                {/* Password Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b pb-2">
                    Change Password
                  </h3>

                  <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Leave password fields blank to keep your current password
                    </p>

                    {/* Current Password */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="current_password"
                        className="text-sm font-medium"
                      >
                        Current Password
                      </Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="current_password"
                          type={showCurrentPassword ? "text" : "password"}
                          placeholder="Enter current password"
                          value={data.user.current_password}
                          onChange={(e) =>
                            setData("user", {
                              ...data.user,
                              current_password: e.target.value,
                            })
                          }
                          className="pl-10 pr-10 h-11"
                          autoComplete="current-password"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowCurrentPassword(!showCurrentPassword)
                          }
                          className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                        >
                          {showCurrentPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* New Password */}
                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-sm font-medium">
                        New Password
                      </Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter new password"
                          value={data.user.password}
                          onChange={(e) =>
                            setData("user", {
                              ...data.user,
                              password: e.target.value,
                            })
                          }
                          className="pl-10 pr-10 h-11"
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Confirm Password */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="password_confirmation"
                        className="text-sm font-medium"
                      >
                        Confirm New Password
                      </Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="password_confirmation"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirm new password"
                          value={data.user.password_confirmation}
                          onChange={(e) =>
                            setData("user", {
                              ...data.user,
                              password_confirmation: e.target.value,
                            })
                          }
                          className="pl-10 pr-10 h-11"
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    {errors.password && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{errors.password}</AlertDescription>
                      </Alert>
                    )}
                  </div>
                </div>
              </CardContent>

              <CardFooter className="flex justify-end space-x-4 pt-6">
                <Button
                  type="submit"
                  className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                  disabled={processing}
                >
                  {processing ? "Saving..." : "Save Changes"}
                </Button>
              </CardFooter>
            </form>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
